import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, errMsg, passwordScore } from "@/lib/api";
import { PasswordMeter } from "@/components/PasswordMeter";
import { Logo } from "@/components/Logo";

const field = "w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const strength = passwordScore(password);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new one");
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md" data-testid="reset-root">
        <Logo size="md" testId="reset-logo" />
        <h1 className="mt-8 text-3xl font-extrabold">Choose a new password</h1>
        {!token && (
          <p className="mt-3 text-sm text-rose-600" data-testid="reset-no-token">
            This link is missing its token. Request a new one from{" "}
            <Link to="/forgot-password" className="font-semibold underline">forgot password</Link>.
          </p>
        )}
        <form onSubmit={submit} className="mt-8 card-flat p-6 space-y-4">
          <div>
            <label className="rule-label">New password</label>
            <input data-testid="reset-password" type="password" className={`${field} mt-2`} value={password} onChange={(e) => setPassword(e.target.value)} required />
            <PasswordMeter value={password} />
          </div>
          <div>
            <label className="rule-label">Confirm new password</label>
            <input data-testid="reset-confirm" type="password" className={`${field} mt-2`} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            {mismatch && <p className="mt-1.5 text-xs text-rose-600">Those two don't match yet.</p>}
          </div>
          <button
            data-testid="reset-submit"
            disabled={busy || !token || !strength.valid || mismatch || !confirm}
            className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> {busy ? "Saving…" : "Set new password"}
            </span>
          </button>
          <p className="text-xs text-slate-400">Setting a new password signs you out everywhere else.</p>
        </form>
      </div>
    </div>
  );
}
